import type { CancelToken } from 'axios';
import axios from 'axios';
import {
  useQuery,
  type UseQueryOptions,
  type UseQueryResult,
  type QueryFunctionContext,
  type QueryKey,
} from 'react-query';

type DebounceQueryOptions<
  TData,
  TQueryKey extends QueryKey,
  TError = unknown,
> = Omit<UseQueryOptions<TData, TError, TData, TQueryKey>, 'queryFn'> & {
  debounceTime?: number;
  onStart?: () => void;
  queryFn: (
    context: QueryFunctionContext<TQueryKey>,
    cancelToken: CancelToken,
  ) => Promise<TData> | TData;
};

function useSuperQuery<
  TData = unknown,
  TQueryKey extends QueryKey = QueryKey,
  TError = unknown,
>(
  options: DebounceQueryOptions<TData, TQueryKey, TError>,
): UseQueryResult<TData, TError> {
  return useQuery<TData, TError, TData, TQueryKey>({
    ...options,
    queryFn: (context: QueryFunctionContext<TQueryKey>) => {
      options.onStart?.();

      let cancelId: ReturnType<typeof setTimeout> | undefined;
      const CancelToken = axios.CancelToken;
      const source = CancelToken.source();

      const promise = new Promise<TData>((resolve, reject) => {
        const run = () => {
          Promise.resolve(options.queryFn(context, source.token))
            .then(resolve)
            .catch(reject);
        };
        if (options.debounceTime == null) return run();
        cancelId = setTimeout(run, options.debounceTime);
      });

      // Optional cancel method
      (promise as any).cancel = () => {
        if (cancelId) clearTimeout(cancelId);
        source.cancel();
      };

      return promise;
    },
  });
}

export default useSuperQuery;
